import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Plus, 
  Package, 
  Barcode,
  Filter,
  RefreshCw,
  History,
  AlertCircle,
  X,
  Settings
} from 'lucide-react';
import { useStore } from '../utils/StoreContext';
import { useAuth } from '../utils/AuthContext';
import { itemService, LocalItem } from '../services/itemService';
import AddItemModal from '../components/AddItemModal';
import ItemDetailsModal from '../components/ItemDetailsModal';
import ItemShelfExpiryModal from '../components/ItemShelfExpiryModal';
import LoadingSpinner from '../components/LoadingSpinner';

const ItemList: React.FC = () => {
  const { currentStore } = useStore();
  const { user } = useAuth();
  const [items, setItems] = useState<LocalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState<LocalItem | null>(null);
  const [movementHistory, setMovementHistory] = useState<any[]>([]);
  const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState<LocalItem | null>(null);
  const [showShelfExpiryModal, setShowShelfExpiryModal] = useState(false);
  const [shelfExpiryItem, setShelfExpiryItem] = useState<LocalItem | null>(null);
  const [stockFilter, setStockFilter] = useState<{
    enabled: boolean;
    operator: 'gt' | 'lt' | 'eq';
    value: number;
  }>({
    enabled: false,
    operator: 'gt',
    value: 0
  });
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [posStockCache, setPosStockCache] = useState<Map<string, {data: any, timestamp: number}>>(new Map());
  
  const POS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  // Load POS stock for a single item
  const loadPosStockForItem = useCallback(async (item: LocalItem, forceRefresh = false) => {
    const now = Date.now();
    const cacheKey = item.sku;
    const cached = posStockCache.get(cacheKey);

    // Mark item as loading
    setItems(prevItems => 
      prevItems.map(prevItem => 
        prevItem.id === item.id 
          ? { ...prevItem, pos_loading: true }
          : prevItem
      )
    );

    try {
      let posResult: { data: any; error?: any };
      
      if (!forceRefresh && cached && (now - cached.timestamp) < POS_CACHE_DURATION) {
        // Use cached data
        posResult = { data: cached.data };
        console.log(`💾 Using cached POS data for ${item.sku}`);
      } else {
        // Fetch fresh data
        console.log(`🌐 Loading POS stock for ${item.sku}`);
        posResult = await itemService.getPosStockLevels(item.sku);
        
        // Update cache
        if (posResult.data) {
          setPosStockCache(prev => new Map(prev.set(cacheKey, {
            data: posResult.data,
            timestamp: now
          })));
        }
      }
      
      if (posResult.data?.product_outlets) {
        // Update this specific item with POS stock levels
        setItems(prevItems => 
          prevItems.map(prevItem => 
            prevItem.id === item.id 
              ? { 
                  ...prevItem, 
                  pos_stock_levels: posResult.data!.product_outlets,
                  pos_loading: false,
                  pos_loaded: true 
                }
              : prevItem
          )
        );
        console.log(`✅ POS stock loaded for ${item.sku}:`, posResult.data.product_outlets.length, 'outlets');
      } else {
        // Mark as loaded but no data found
        setItems(prevItems => 
          prevItems.map(prevItem => 
            prevItem.id === item.id 
              ? { 
                  ...prevItem, 
                  pos_loading: false,
                  pos_loaded: true 
                }
              : prevItem
          )
        );
        console.log(`⚪ No POS stock found for ${item.sku}`);
      }
    } catch (error) {
      console.error(`❌ Error loading POS stock for ${item.sku}:`, error);
      setItems(prevItems => 
        prevItems.map(prevItem => 
          prevItem.id === item.id 
            ? { 
                ...prevItem, 
                pos_loading: false,
                pos_loaded: true 
              }
            : prevItem
        )
      );
    }
  }, [posStockCache, POS_CACHE_DURATION]);


  // Fetch items from local database
  const fetchItems = useCallback(async (searchQuery: string = '', pageNum: number = 1, append: boolean = false) => {
    console.log('🔍 fetchItems called with store:', currentStore?.id, 'search:', searchQuery, 'page:', pageNum);
    setLoading(true);
    setError(null);

    try {
      let result: { data: LocalItem[] | null; error: any };
      if (searchQuery.trim()) {
        // Search items with pagination
        console.log('🔍 Searching items for store:', currentStore?.id);
        result = await itemService.searchLocalItems(searchQuery, currentStore?.id, pageNum, 20, stockFilter);
      } else {
        // Get all items
        console.log('🔍 Getting all items for store:', currentStore?.id);
        result = await itemService.getLocalItems(currentStore?.id, pageNum, 20, stockFilter);
      }

      if (result.error) {
        setError('Failed to fetch items');
        console.error('Error fetching items:', result.error);
      } else if (result.data && Array.isArray(result.data)) {
        const itemsData = result.data as LocalItem[];
        console.log('🔍 Fetched', itemsData.length, 'items for store:', currentStore?.id);
        if (append && pageNum > 1) {
          setItems(prev => [...prev, ...itemsData]);
        } else {
          setItems(itemsData);
        }
        // For search results, assume more if we got a full page
        // For regular items, this logic already works correctly
        setHasMore(itemsData.length === 20);
        
        // POS stock enhancement is now manual only
      }
    } catch (err) {
      setError('Failed to fetch items');
      console.error('Error fetching items:', err);
    } finally {
      setLoading(false);
    }
  }, [currentStore?.id]);

  // Fetch items with specific filter (to avoid stale state issues)
  const fetchItemsWithFilter = useCallback(async (searchQuery: string = '', pageNum: number = 1, append: boolean = false, filter: typeof stockFilter) => {
    setLoading(true);
    setError(null);

    try {
      let result: { data: LocalItem[] | null; error: any };
      if (searchQuery.trim()) {
        // Search items with pagination
        result = await itemService.searchLocalItems(searchQuery, currentStore?.id, pageNum, 20, filter);
      } else {
        // Get all items
        result = await itemService.getLocalItems(currentStore?.id, pageNum, 20, filter);
      }

      if (result.error) {
        setError('Failed to fetch items');
        console.error('Error fetching items:', result.error);
      } else if (result.data && Array.isArray(result.data)) {
        const itemsData = result.data as LocalItem[];
        if (append && pageNum > 1) {
          setItems(prev => [...prev, ...itemsData]);
        } else {
          setItems(itemsData);
        }
        // For search results, assume more if we got a full page
        // For regular items, this logic already works correctly
        setHasMore(itemsData.length === 20);
        
        // POS stock enhancement is now manual only
      }
    } catch (err) {
      setError('Failed to fetch items');
      console.error('Error fetching items:', err);
    } finally {
      setLoading(false);
    }
  }, [currentStore?.id]);

  // Load items on component mount and when store/filter changes
  useEffect(() => {
    console.log('🏪 Store or filter changed, fetching items for store:', currentStore?.id);
    setPage(1);
    setSearchTerm(''); // Clear search when store changes
    setItems([]); // Clear items immediately for better UX
    
    // Reset POS stock cache when store changes
    setPosStockCache(new Map());
    
    fetchItems(); // Fetch items for new store
  }, [currentStore?.id, stockFilter]); // Removed fetchItems dependency to prevent cycles

  // Debounced search function
  const debouncedSearch = useCallback((searchQuery: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      setPage(1);
      fetchItems(searchQuery, 1, false);
    }, 300); // Wait 300ms after user stops typing
    
    setSearchTimeout(newTimeout);
  }, [searchTimeout]); // Removed fetchItems dependency

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() === '') {
      // If search is cleared, immediately fetch all items
      setPage(1);
      fetchItems('', 1, false);
    } else {
      // Otherwise, use debounced search
      debouncedSearch(value);
    }
  };

  // Handle search form submit (for enter key)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setPage(1);
    fetchItems(searchTerm, 1, false);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
    fetchItems('', 1, false);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setPage(1);
    fetchItems('', 1, false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle load more
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchItems(searchTerm, nextPage, true);
  };

  // Handle item added from modal
  const handleItemAdded = () => {
    // Refresh the list after adding an item
    fetchItems(searchTerm, 1, false);
    setPage(1);
  };

  // Handle viewing movement history
  const handleViewHistory = async (item: LocalItem) => {
    setSelectedItemForHistory(item);
    setLoading(true);
    
    try {
      const result = await itemService.getItemMovementHistory(item.id, currentStore?.id);
      if (result.error) {
        setError('Failed to fetch movement history');
        console.error('Movement history error:', result.error);
      } else {
        setMovementHistory(result.data || []);
        setShowHistoryModal(true);
      }
    } catch (err) {
      setError('Failed to fetch movement history');
      console.error('Movement history error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle shelf and expiry management
  const handleManageShelfExpiry = useCallback((item: LocalItem) => {
    setShelfExpiryItem(item);
    setShowShelfExpiryModal(true);
  }, []);

  const handleShelfExpiryUpdate = useCallback((updatedItem: LocalItem) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      )
    );
    // Also update the modal's item state to sync with the changes
    setShelfExpiryItem(updatedItem);
  }, []);

  // Handle opening item details modal
  const handleItemClick = (item: LocalItem) => {
    setSelectedItemForDetails(item);
    setShowItemDetailsModal(true);
  };

  // Handle item updated (refresh list)
  const handleItemUpdated = () => {
    fetchItems(searchTerm, 1, false);
    setPage(1);
  };

  // Handle item deleted (refresh list and close modal)
  const handleItemDeleted = () => {
    setShowItemDetailsModal(false);
    setSelectedItemForDetails(null);
    fetchItems(searchTerm, 1, false);
    setPage(1);
  };

  // Handle stock filter changes with optimized refresh
  const handleStockFilterChange = useCallback((filter: typeof stockFilter) => {
    setStockFilter(filter);
    setPage(1);
    // Use a small delay to prevent excessive API calls during rapid filter changes
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    const timeout = setTimeout(() => {
      // Call the API directly with the new filter to avoid stale state
      fetchItemsWithFilter(searchTerm, 1, false, filter);
    }, 300);
    setSearchTimeout(timeout);
  }, [searchTerm, searchTimeout, fetchItemsWithFilter]);

  const clearStockFilter = useCallback(() => {
    const clearedFilter = { enabled: false, operator: 'gt' as const, value: 0 };
    setStockFilter(clearedFilter);
    setPage(1);
    // Use the cleared filter directly to avoid stale state
    fetchItemsWithFilter(searchTerm, 1, false, clearedFilter);
  }, [searchTerm, fetchItemsWithFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
            Item List
          </h1>
          <p className="text-gray-500">
            {currentStore ? `Items for ${currentStore.name}` : 'Select a store to view items'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {user?.user_type === 'Admin' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          )}
        </div>
      </div>

      {/* Search - Single persistent input to maintain focus */}
      <div className={searchTerm ? "card" : "relative"}>
        <div className={searchTerm ? "flex items-center gap-3" : ""}>
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchTerm ? "Search by name, SKU, barcode, category, or brand..." : "Search items..."}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white shadow-sm transition-all duration-200 hover:border-gray-300"
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="btn-secondary px-4 py-3 text-sm flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="text-sm text-gray-500 bg-blue-50 px-4 py-2 rounded-md border border-blue-100 mt-3">
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-500" />
              {loading ? 'Searching...' : `Searching for: "${searchTerm}"`}
            </span>
          </div>
        )}
      </div>


      {/* Store Filter Info with Stock Filter */}
      {currentStore && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                Showing items for: {currentStore.name}
              </span>
              {stockFilter.enabled && (
                <div className="flex items-center gap-1 text-sm text-blue-700 bg-blue-100 px-3 py-1 rounded-full border border-blue-200 ml-2 shadow-sm">
                  <Filter className="w-3 h-3" />
                  <span className="font-medium">
                    Stock {stockFilter.operator === 'gt' ? '>' : stockFilter.operator === 'lt' ? '<' : '='} {stockFilter.value}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowFilterDialog(true)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Stock Filter</span>
              <span className="sm:hidden">Filter</span>
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="card">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No items found' : 'No items available'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? 'Try adjusting your search terms or add new items from the POS system.'
                : user?.user_type === 'Admin' 
                  ? 'Start by adding items from your POS system.'
                  : 'Contact an administrator to add items.'
              }
            </p>
            {user?.user_type === 'Admin' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Items Grid - Modern Hero Image Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group cursor-pointer" onClick={() => handleItemClick(item)}>
                  {/* Hero Image */}
                  <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    {/* Display primary image if available */}
                    {item.primary_image ? (
                      <img
                        src={item.primary_image}
                        alt={item.name}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          // Hide image on error and show fallback
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : null}
                    
                    {/* Fallback icon when no image or image fails */}
                    <div className={`absolute inset-0 flex items-center justify-center ${item.primary_image ? 'hidden' : 'flex'}`}>
                      <Package className="w-12 h-12 text-gray-400 group-hover:text-gray-500 transition-colors" />
                    </div>
                    
                    {/* Price badge - top left */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                        MVR {item.latest_unit_price ? item.latest_unit_price.toFixed(2) : (item.average_cost * 1.3).toFixed(0)}
                      </span>
                    </div>
                    
                    {/* Status indicator - top right */}
                    <div className="absolute top-3 right-3">
                      <div className={`w-3 h-3 rounded-full ${
                        item.is_active ? 'bg-green-500' : 'bg-gray-400'
                      } shadow-sm`} />
                    </div>

                    {/* Category badge - bottom */}
                    {item.category && (
                      <div className="absolute bottom-3 left-3">
                        <span className="bg-black/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                          {item.category}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Title and Brand */}
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
                        {item.name}
                      </h3>
                        {item.brand && (
                          <p className="text-xs text-gray-500 font-medium">{item.brand}</p>
                        )}
                        {/* Shelf Tags */}
       {item.shelves && item.shelves.length > 0 && (
         <div className="flex flex-wrap gap-1 mt-2">
           {item.shelves.map((shelf, index) => (
             <span 
               key={shelf.id || index}
               className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium"
             >
               {shelf.shelf_name}
             </span>
           ))}
         </div>
       )}
                        {/* Description */}
                        {item.description && item.description.trim() && (
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>

                    {/* SKU and Stock */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">SKU:</span>
                          <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-700">
                            {item.sku}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Stock:</span>
                          <span className={`text-lg font-bold px-3 py-1 rounded-full ${
                            item.current_stock > 10 
                              ? 'bg-green-100 text-green-700' 
                              : item.current_stock > 0
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {item.current_stock}
                          </span>
                        </div>
                      </div>
                      
                      {item.barcode && (
                        <div className="flex items-center gap-2">
                          <Barcode className="w-3 h-3 text-gray-400" />
                          <span className="text-xs font-mono text-gray-600">{item.barcode}</span>
                        </div>
                      )}
                    </div>

                    {/* POS Stock Section */}
                    <div className="space-y-2 mb-3 pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-blue-600">POS Stock:</span>
                        
                        {!item.pos_loaded && !item.pos_loading && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadPosStockForItem(item);
                            }}
                            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Load
                          </button>
                        )}
                        
                        {item.pos_loading && (
                          <div className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3 text-blue-500 animate-spin" />
                            <span className="text-xs text-blue-500">Loading...</span>
                          </div>
                        )}
                        
                        {item.pos_loaded && !item.pos_loading && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              loadPosStockForItem(item, true);
                            }}
                            className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Refresh
                          </button>
                        )}
                      </div>

                      {/* POS Stock Data */}
                      {item.pos_loaded && (
                        <div>
                          {item.pos_stock_levels && item.pos_stock_levels.length > 0 ? (
                            <div className="space-y-1">
                              {item.pos_stock_levels.map((outlet, index) => (
                                <div key={index} className="flex justify-between items-center text-xs">
                                  <span className="text-gray-600 font-medium">{outlet.outlet_name}:</span>
                                  <span className={`font-medium px-2 py-0.5 rounded ${
                                    outlet.available_inventory > 0 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {outlet.available_inventory} units
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 italic">
                              Not found in POS system
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageShelfExpiry(item);
                          }}
                          className="text-purple-600 hover:text-purple-700 text-xs font-medium flex items-center gap-1 px-2 py-1 rounded-md hover:bg-purple-50 transition-colors"
                        >
                          <Settings className="w-3 h-3" />
                          Manage
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewHistory(item);
                          }}
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium flex items-center gap-1 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                        >
                          <History className="w-3 h-3" />
                          History
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && !loading && (
              <div className="text-center pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={handleLoadMore}
                  className="btn-secondary"
                >
                  Load More Items
                </button>
              </div>
            )}

            {/* Loading More Indicator */}
            {loading && items.length > 0 && (
              <div className="text-center pt-6 mt-6 border-t border-gray-200">
                <LoadingSpinner />
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onItemAdded={handleItemAdded}
        />
      )}

      {/* Item Details Modal */}
      {showItemDetailsModal && selectedItemForDetails && (
        <ItemDetailsModal
          item={selectedItemForDetails}
          onClose={() => {
            setShowItemDetailsModal(false);
            setSelectedItemForDetails(null);
          }}
          onItemUpdated={handleItemUpdated}
          onItemDeleted={handleItemDeleted}
        />
      )}

      {/* Shelf & Expiry Management Modal */}
      {showShelfExpiryModal && shelfExpiryItem && (
        <ItemShelfExpiryModal
          isOpen={showShelfExpiryModal}
          item={shelfExpiryItem}
          onClose={() => {
            setShowShelfExpiryModal(false);
            setShelfExpiryItem(null);
          }}
          onUpdate={handleShelfExpiryUpdate}
        />
      )}

      {/* Movement History Modal */}
      {showHistoryModal && selectedItemForHistory && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm sm:max-w-lg md:max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                  <History className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Movement History</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">{selectedItemForHistory.name}</p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs text-gray-500">
                    <span className="truncate">SKU: {selectedItemForHistory.sku}</span>
                    <span className="flex-shrink-0">Stock: <span className="font-semibold text-blue-600">{selectedItemForHistory.current_stock}</span></span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedItemForHistory(null);
                  setMovementHistory([]);
                }}
                className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors flex-shrink-0 ml-2"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-140px)]">
              {movementHistory.length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                    <History className="w-12 h-12 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Movement History</h4>
                  <p className="text-gray-500">No stock movements found for this item.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Desktop Header (hidden on mobile) */}
                  <div className="hidden md:block bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-600 uppercase tracking-wide">
                      <div className="col-span-2">Date & Time</div>
                      <div className="col-span-2">Movement Type</div>
                      <div className="col-span-2">Reference</div>
                      <div className="col-span-1 text-center">Quantity</div>
                      <div className="col-span-1 text-center">Balance</div>
                      <div className="col-span-1 text-right">Unit Cost</div>
                      <div className="col-span-1 text-right">Unit Price</div>
                      <div className="col-span-2">Remarks</div>
                    </div>
                  </div>

                  {/* Movement List */}
                  <div className="space-y-3">
                    {movementHistory.map((movement: any, index: number) => (
                      <div key={movement.id}>
                        {/* Mobile Layout (default, hidden on md+) */}
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:hidden">
                          {/* Header Row - Type & Date */}
                          <div className="flex items-center justify-between mb-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              movement.movement_type === 'opening_stock'
                                ? 'bg-purple-100 text-purple-800'
                                : movement.movement_type === 'purchase_in' 
                                  ? 'bg-green-100 text-green-800' 
                                  : movement.movement_type === 'adjustment'
                                    ? 'bg-blue-100 text-blue-800'
                                    : movement.movement_type === 'delivery_out'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-gray-100 text-gray-800'
                            }`}>
                              {movement.movement_type === 'opening_stock' ? 'OPENING' :
                               movement.movement_type === 'purchase_in' ? 'PURCHASE' :
                               movement.movement_type === 'delivery_out' ? 'DELIVERY' :
                               movement.movement_type === 'adjustment' ? 'ADJUST' :
                               movement.movement_type.replace('_', ' ').toUpperCase()}
                            </span>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {new Date(movement.movement_date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </div>
                              <div className="text-xs text-blue-600 font-mono">
                                {movement.time_only || '00:00'}
                              </div>
                            </div>
                          </div>

                          {/* Simple Transaction Table */}
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            {/* Table Header */}
                            <div className="grid grid-cols-3 gap-2 mb-2 pb-2 border-b border-gray-300">
                              <div className="text-xs font-medium text-gray-600 text-center">Started</div>
                              <div className="text-xs font-medium text-gray-600 text-center">Movement</div>
                              <div className="text-xs font-medium text-gray-600 text-center">Balance</div>
                            </div>
                            
                            {/* Transaction Row */}
                            <div className="grid grid-cols-3 gap-2">
                              {/* Started */}
                              <div className="text-center">
                                <div className="bg-gray-100 border border-gray-300 rounded px-2 py-2">
                                  <span className="text-lg font-bold text-gray-700">
                                    {movement.balance_before || 0}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Movement */}
                              <div className="text-center">
                                <div className={`border rounded px-2 py-2 ${
                                  movement.transaction_type === 'IN' || movement.quantity_change > 0
                                    ? 'bg-green-50 border-green-300'
                                    : 'bg-red-50 border-red-300'
                                }`}>
                                  <span className={`text-lg font-bold ${
                                    movement.transaction_type === 'IN' || movement.quantity_change > 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }`}>
                                    {movement.quantity_change !== undefined ?
                                      (movement.quantity_change > 0 ? '+' : '') + movement.quantity_change :
                                      (movement.quantity > 0 ? '+' : '') + movement.quantity
                                    }
                                  </span>
                                </div>
                              </div>
                              
                              {/* Balance */}
                              <div className="text-center">
                                <div className="bg-blue-50 border border-blue-300 rounded px-2 py-2">
                                  <span className="text-lg font-bold text-blue-600">
                                    {movement.running_balance || movement.balance_after || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Footer Info */}
                          <div className="flex items-center justify-between text-xs">
                            <div className="font-mono text-gray-700">
                              {movement.reference_number}
                            </div>
                            <div className="text-gray-500">
                              by {movement.created_by_phone || 'System'}
                            </div>
                          </div>

                          {/* Expandable Details */}
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div>
                                <span className="text-gray-500">Cost:</span>
                                <span className="ml-1 font-medium">MVR {parseFloat(movement.unit_cost || 0).toFixed(0)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Price:</span>
                                <span className="ml-1 font-medium">MVR {parseFloat(movement.unit_price || 0).toFixed(0)}</span>
                              </div>
                            </div>
                            {movement.remarks && movement.remarks !== 'No remarks' && (
                              <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
                                {movement.remarks}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Desktop Layout (hidden on mobile) */}
                        <div className="hidden md:block">
                          <div className="relative">
                            {/* Timeline line for desktop */}
                            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-gray-200 to-gray-300"></div>
                            
                            <div className="relative pl-16 pb-6">
                              {/* Timeline dot */}
                              <div className={`absolute left-6 w-4 h-4 rounded-full border-2 ${
                                movement.movement_type === 'opening_stock'
                                  ? 'border-purple-500 bg-purple-100'
                                  : movement.movement_type === 'purchase_in' 
                                    ? 'border-green-500 bg-green-100' 
                                    : movement.movement_type === 'adjustment'
                                      ? 'border-blue-500 bg-blue-100'
                                      : movement.movement_type === 'delivery_out'
                                        ? 'border-orange-500 bg-orange-100'
                                        : 'border-gray-500 bg-gray-100'
                              }`}>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  {movement.movement_type === 'opening_stock' && (
                                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Desktop Movement Card */}
                              <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                <div className="grid grid-cols-12 gap-4 p-4 items-center">
                                  {/* Date & Time */}
                                  <div className="col-span-2">
                                    <div className="text-sm font-medium text-gray-900">
                                      {new Date(movement.movement_date).toLocaleDateString()}
                                    </div>
                                    <div className="text-xs text-blue-600 font-mono">
                                      {movement.time_only || (movement.movement_datetime ? 
                                        new Date(movement.movement_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 
                                        new Date(movement.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                      by {movement.created_by_phone || 'System'}
                                    </div>
                                  </div>

                                  {/* Movement Type */}
                                  <div className="col-span-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      movement.movement_type === 'opening_stock'
                                        ? 'bg-purple-100 text-purple-800'
                                        : movement.movement_type === 'purchase_in' 
                                          ? 'bg-green-100 text-green-800' 
                                          : movement.movement_type === 'adjustment'
                                            ? 'bg-blue-100 text-blue-800'
                                            : movement.movement_type === 'delivery_out'
                                              ? 'bg-orange-100 text-orange-800'
                                              : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {movement.movement_type === 'opening_stock' 
                                        ? 'OPENING STOCK'
                                        : movement.movement_type === 'purchase_in'
                                          ? 'PURCHASE IN'
                                          : movement.movement_type === 'delivery_out'
                                            ? 'DELIVERY OUT'
                                            : movement.movement_type === 'adjustment'
                                              ? 'ADJUSTMENT'
                                              : movement.movement_type.replace('_', ' ').toUpperCase()
                                      }
                                    </span>
                                    <div className="text-xs text-gray-500 mt-1">{movement.store_name}</div>
                                  </div>

                                  {/* Reference */}
                                  <div className="col-span-2">
                                    <div className="text-sm font-mono text-gray-800">
                                      {movement.reference_number}
                                    </div>
                                  </div>

                                  {/* Quantity */}
                                  <div className="col-span-1 text-center">
                                    <span className={`text-sm font-bold ${
                                      movement.transaction_type === 'IN' ? 'text-green-600' : 
                                      movement.transaction_type === 'OUT' ? 'text-red-600' : 
                                      movement.quantity > 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                      {movement.quantity_change !== undefined ? 
                                        (movement.quantity_change > 0 ? '+' : '') + movement.quantity_change :
                                        (movement.quantity > 0 ? '+' : '') + movement.quantity
                                      }
                                    </span>
                                    {movement.transaction_type && (
                                      <div className={`text-xs font-medium mt-1 ${
                                        movement.transaction_type === 'IN' ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {movement.transaction_type}
                                      </div>
                                    )}
                                  </div>

                                  {/* Running Balance */}
                                  <div className="col-span-1 text-center">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                                      <span className="text-sm font-bold text-blue-700">
                                        {movement.running_balance || movement.balance_after || 0}
                                      </span>
                                    </div>
                                    {movement.balance_before !== undefined && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Before: {movement.balance_before}
                                      </div>
                                    )}
                                  </div>

                                  {/* Unit Cost */}
                                  <div className="col-span-1 text-right">
                                    <div className="text-sm text-gray-900">
                                      MVR {parseFloat(movement.unit_cost || 0).toFixed(2)}
                                    </div>
                                  </div>

                                  {/* Unit Price */}
                                  <div className="col-span-1 text-right">
                                    <div className="text-sm text-gray-900">
                                      MVR {parseFloat(movement.unit_price || 0).toFixed(2)}
                                    </div>
                                  </div>

                                  {/* Remarks */}
                                  <div className="col-span-2">
                                    <div className="text-sm text-gray-600">
                                      {movement.remarks || 'No remarks'}
                                    </div>
                                    {movement.total_amount && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Total: MVR {parseFloat(movement.total_amount).toFixed(2)}
                                      </div>
                                    )}
                                    {movement.order_references && (
                                      <div className="text-xs text-gray-400 mt-1 font-mono">
                                        {movement.order_references}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                Total movements: <span className="font-semibold">{movementHistory.length}</span>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedItemForHistory(null);
                  setMovementHistory([]);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors font-medium shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Filter Dialog */}
      {showFilterDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Stock Filter</h3>
                <p className="text-sm text-gray-500 mt-1">Filter items by stock levels</p>
              </div>
              <button
                onClick={() => setShowFilterDialog(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Filter Toggle */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={stockFilter.enabled}
                      onChange={(e) => {
                        const newFilter = {
                          ...stockFilter,
                          enabled: e.target.checked
                        };
                        setStockFilter(newFilter);
                        setPage(1);
                        // Apply filter immediately without debounce for checkbox toggle
                        fetchItemsWithFilter(searchTerm, 1, false, newFilter);
                      }}
                      className="w-4 h-4 rounded border-blue-300 text-blue-600 shadow-sm focus:border-blue-400 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <div>
                      <span className="text-sm font-medium text-blue-900">Enable Stock Filtering</span>
                      <p className="text-xs text-blue-700 mt-0.5">Filter items based on their current stock levels</p>
                    </div>
                  </label>
                </div>

                {/* Filter Controls */}
                {stockFilter.enabled && (
                  <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                    {/* Filter Condition */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Filter Condition
                      </label>
                      <select
                        value={stockFilter.operator}
                        onChange={(e) => {
                          const newFilter = {
                            ...stockFilter,
                            operator: e.target.value as 'gt' | 'lt' | 'eq'
                          };
                          setStockFilter(newFilter);
                          setPage(1);
                          // Apply filter immediately for dropdown changes
                          fetchItemsWithFilter(searchTerm, 1, false, newFilter);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-colors"
                      >
                        <option value="gt">Greater than</option>
                        <option value="lt">Less than</option>
                        <option value="eq">Equal to</option>
                      </select>
                    </div>

                    {/* Stock Quantity */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Stock Quantity
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={stockFilter.value}
                        onChange={(e) => {
                          const newFilter = {
                            ...stockFilter,
                            value: parseInt(e.target.value) || 0
                          };
                          // Use debounced handler for number input to avoid excessive API calls while typing
                          handleStockFilterChange(newFilter);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-colors"
                        placeholder="Enter stock quantity"
                      />
                    </div>

                    {/* Filter Preview */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Filter className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-green-900">Active Filter</div>
                          <div className="text-sm text-green-700 mt-1">
                            Show items with stock {stockFilter.operator === 'gt' ? 'greater than' : stockFilter.operator === 'lt' ? 'less than' : 'equal to'} <span className="font-semibold">{stockFilter.value}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  const clearedFilter = { enabled: false, operator: 'gt' as const, value: 0 };
                  setStockFilter(clearedFilter);
                  setPage(1);
                  // Apply cleared filter immediately
                  fetchItemsWithFilter(searchTerm, 1, false, clearedFilter);
                  setShowFilterDialog(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Clear Filter
              </button>
              <button
                onClick={() => setShowFilterDialog(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors font-medium shadow-sm"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;